import React from 'react';

import styled, { css } from 'styled-components';

import {
  AutoSuggestList,
  AutoSuggestListItem,
  AutoSuggestSection,
  AutoSuggestSectionTitle,
} from '~/searchCio/components/sharedComponents/productComponents';
import { QUERY_TYPE_RECENT_SEARCHES } from '~/searchCio/constants/cioConstants';
import useCioAutoSuggest from '~/searchCio/hooks/useCioAutosuggest';
import { useCioSearchHistory } from '~/searchCio/hooks/useCioSearchHistory';
import {
  constructorTextStandard,
  constructorTextHighlight,
} from '~/searchCio/utils/styles';
import { Button, mobile } from '~/techstyle-shared/react-components';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
} from '~/techstyle-shared/react-intl';

const clearHistoryLabelStyle = css`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: right;
`;

const ClearHistoryButton = styled(Button).attrs({
  variant: 'unstyled',
  labelStyle: clearHistoryLabelStyle,
})`
  ${constructorTextStandard}
  ${constructorTextHighlight}
  text-transform: none;
  display: inline-flex;
  flex-direction: column;
  line-height: 1.2;
  flex: 0 0 auto;

  &:before {
    inset: -8px;
  }
`;

const TitleRowWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  text-transform: uppercase;
  padding: 0;
  margin: 0;
  cursor: default;
  justify-content: flex-start;

  ${mobile`
    justify-content: space-between;
  `}
`;

const messages = defineMessages({
  clearAllAriaLabel: {
    id: 'search_autosuggest.clear_all_aria_label',
    defaultMessage: '"clear all recent search history"',
  },
});

const RecentSearchesSection = () => {
  const { history, clearHistory } = useCioSearchHistory();
  const { handleSearchTermSelect } = useCioAutoSuggest();
  const intl = useIntl();

  if (!history || history.length === 0) {
    return null;
  }

  return (
    <AutoSuggestSection data-testid="cnstrc-recent-searches">
      <TitleRowWrapper>
        <AutoSuggestSectionTitle role="heading" id="recent_searches_title">
          <FormattedMessage
            id="search_autosuggest.recent_searches_header"
            defaultMessage="Recent Searches"
          />
        </AutoSuggestSectionTitle>
        <ClearHistoryButton
          type="button"
          onClick={clearHistory}
          data-autotag="topnav_search_clear"
          aria-label={intl.formatMessage(messages.clearAllAriaLabel)}
          data-testid="cnstrc-search-clear"
        >
          <FormattedMessage
            id="search_autosuggest.clear_all_recent_searches_button_label"
            defaultMessage="Clear All"
          />
        </ClearHistoryButton>
      </TitleRowWrapper>

      <AutoSuggestList aria-labelledby="recent_searches_title">
        {history.map((recentSearchTerm, index) => {
          return (
            <AutoSuggestListItem
              role="link"
              data-autotag="search_suggestions_list_item"
              key={recentSearchTerm}
              tabIndex={0}
              onClick={() => {
                handleSearchTermSelect({
                  searchTerm: recentSearchTerm,
                  queryType: QUERY_TYPE_RECENT_SEARCHES,
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchTermSelect({
                    searchTerm: recentSearchTerm,
                    queryType: QUERY_TYPE_RECENT_SEARCHES,
                  });
                }
              }}
              data-cnstrc-item-section="Recent Searches"
              data-testid={`cnstrc-recent-search-${index}`}
            >
              {recentSearchTerm}
            </AutoSuggestListItem>
          );
        })}
      </AutoSuggestList>
    </AutoSuggestSection>
  );
};

export default RecentSearchesSection;
